import { ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'

/**
 * The one card the home page uses for anything bookable — the three family
 * cards (enduro / treks / climbing) and every activity tile below them. They
 * used to be two different designs on the same screen.
 *
 * Shape: image with a "From $X" pill, then title, blurb and a text CTA.
 */
export type AdventureCardProps = {
  /** Internal route. Use `href` instead for a mailto: or external target. */
  to?: string
  href?: string
  title: string
  body?: string | null
  cta: string
  /** Cheapest price in the family; the pill is hidden when null. */
  priceFrom?: number | null
  /** e.g. "per tuk tuk" — only when the unit isn't per person. */
  priceNote?: string
  fromLabel?: string
  image?: string | null
  /** Use when the caller needs its own <img> (crop settings, lazy variants). */
  imageNode?: React.ReactNode
  alt?: string
  /** Gradient behind the icon when there's no photo. */
  tint?: string
  icon?: LucideIcon
  /** Overrides the price pill, e.g. "Coming back soon". */
  badge?: string
  /** Greys the photo out for something not currently bookable. */
  muted?: boolean
  index?: number
  reduceMotion?: boolean
}

export default function AdventureCard({
  to,
  href,
  title,
  body,
  cta,
  priceFrom = null,
  priceNote,
  fromLabel = 'From',
  image,
  imageNode,
  alt,
  tint = 'from-garage-primary to-garage-accent',
  icon: Icon,
  badge,
  muted = false,
  index = 0,
  reduceMotion = false,
}: AdventureCardProps) {
  const inner = (
    <>
      <div className={`relative h-52 overflow-hidden bg-gradient-to-br ${tint}`}>
        {imageNode ??
          (image ? (
            <img
              src={image}
              alt={alt ?? title}
              className={`h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                muted ? 'grayscale' : ''
              }`}
              loading="lazy"
            />
          ) : (
            Icon && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon className="h-20 w-20 text-white/30" />
              </div>
            )
          ))}
        {badge ? (
          <div className="absolute right-4 top-4 rounded-full bg-yellow-400 px-4 py-2 text-xs font-bold uppercase tracking-wide text-black shadow-lg">
            {badge}
          </div>
        ) : (
          priceFrom != null && (
            <div className="absolute right-4 top-4 rounded-full bg-garage-accent px-4 py-2 text-base font-bold text-garage-cream shadow-lg">
              {fromLabel} ${priceFrom}
              {priceNote && (
                <span className="ml-1 text-xs font-semibold opacity-90">
                  {priceNote}
                </span>
              )}
            </div>
          )
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="mb-3 font-garage-title text-2xl font-bold text-garage-dark transition-colors group-hover:text-garage-accent">
          {title}
        </h3>
        {body && (
          <p className="mb-6 line-clamp-4 flex-1 text-garage-text">{body}</p>
        )}
        <span className="mt-auto inline-flex items-center gap-2 font-bold text-garage-accent">
          {cta}
          <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </>
  )

  const shell =
    'group h-full flex flex-col overflow-hidden rounded-2xl border-2 border-garage-border bg-white transition-all duration-200 hover:-translate-y-1 hover:border-garage-accent hover:shadow-garage-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-garage-accent'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: reduceMotion ? 0.2 : 0.4,
        delay: reduceMotion ? 0 : index * 0.1,
      }}
      className="h-full"
    >
      {href ? (
        <a href={href} className={shell}>
          {inner}
        </a>
      ) : (
        <Link to={to ?? '#'} className={shell}>
          {inner}
        </Link>
      )}
    </motion.div>
  )
}
